import { formatMoney } from '@theme/utilities';
import { ThemeEvents } from '@theme/events';

// Prix affiché par l'app AOV (« HUF 42,920 » ou « 42.920 Ft ») en fillér (centièmes).
function parseCents(text) {
  const digits = (text || '').replace(/[^\d.,]/g, '');
  if (!digits) return null;
  // 1 ou 2 décimales (« HUF 54,799.5 » quand une remise de 15 % tombe sur un demi-forint)
  const match = digits.match(/^(.*?)[.,](\d{1,2})$/);
  const cents = match
    ? Number(match[1].replace(/[.,]/g, '') || 0) * 100 + Number(match[2].padEnd(2, '0'))
    : Number(digits.replace(/[.,]/g, '')) * 100;
  return Number.isFinite(cents) && cents > 0 ? cents : null;
}

class StickyAddToCart extends HTMLElement {
  constructor() {
    super();

    this.form = null;
    this.footer = null;
    this.content = null;
    this.formInView = true;
    this.footerInView = false;
    this.formObserver = null;
    this.footerObserver = null;
  }

  connectedCallback() {
    this.form = this.closest('[data-ref="product-form"]');
    this.footer = document.querySelector('[data-ref="footer"]');
    this.content = this.querySelector('.sticky-add-to-cart__content');

    if (!this.form || !this.content) return;

    // Réserve l'espace du sticky dans le flux pour éviter le changement de
    // hauteur du document quand le contenu passe en position: fixed. Sans cela,
    // le contenu sort du flux → la page raccourcit → le footer remonte dans le
    // viewport → le sticky se désactive → boucle infinie de clignotement.
    requestAnimationFrame(() => {
      this.style.minHeight = `${this.offsetHeight}px`;
      this.#initObservers();
    });

    // Mennyezeti Lampa : la barre reprend le palier de remise choisi (2, 3, 4 darab) et la variante,
    // car c'est ce que le bouton ajoute au panier.
    this.onQuantityBreaks = () => this.#syncBundle();
    this.onVariant = (event) => this.#onVariantUpdated(event);
    document.addEventListener(ThemeEvents.quantityBreaksUpdated, this.onQuantityBreaks);
    document.addEventListener(ThemeEvents.variantUpdated, this.onVariant);
    this.#watchAov();
    this.#syncBundle();
  }

  // Widget de remise AOV (comme sur applique-design) : on suit le palier choisi et on affiche ses prix
  // au format de la boutique (l'app écrit « HUF 42,920 »).
  #watchAov() {
    const root = this.closest('.product-section__product-info') || this.form;
    if (!root) return;

    let timer;
    this.aovObserver = new MutationObserver(() => {
      clearTimeout(timer);
      timer = setTimeout(() => {
        this.#formatAovPrices(root);
        this.#syncBundle();
      }, 60);
    });
    this.aovObserver.observe(root, {
      subtree: true,
      childList: true,
      characterData: true,
      attributes: true,
      attributeFilter: ['class'],
    });
    this.#formatAovPrices(root);
  }

  #formatAovPrices(root) {
    root.querySelectorAll('aov-bundle-widget, .AOV-AutoMount').forEach((widget) => {
      // « 5% kedvezmény » devient la pastille « -5% » (comme les anciennes cases du thème)
      widget.querySelectorAll('.Avada-Volume__DiscountText').forEach((element) => {
        const match = element.textContent.match(/^\s*(\d+)\s*%/);
        const text = match ? `-${match[1]}%` : null;
        if (text && element.textContent !== text) element.textContent = text;
      });
      // Prix affichés par l'app, quel que soit son format (« HUF 36,490.00 », « 36 490,00 HUF »…)
      widget.querySelectorAll('.AOV-Offer__DiscountPrice, .AOV-Offer__BasePrice').forEach((element) => {
        if (!element.textContent.includes('HUF')) return;
        const cents = parseCents(element.textContent.replace(/[\s\u00a0\u202f]/g, ''));
        const text = cents ? formatMoney(cents) : null;
        if (text && element.textContent !== text) element.textContent = text;
      });
      const walker = document.createTreeWalker(widget, NodeFilter.SHOW_TEXT);
      let node;
      while ((node = walker.nextNode())) {
        if (!node.nodeValue.includes('HUF')) continue;
        // Textes pour lecteurs d'écran (« Price … », « Was …, now … ») en hongrois au passage.
        const text = node.nodeValue
          .replace(/HUF\s*([\d.,]+)/g, (all, amount) => {
            const cents = parseCents(amount);
            return cents ? formatMoney(cents) : all;
          })
          .replace(/^\s*Price\b/, 'Ár:')
          .replace(/^\s*Was\b/, 'Eredeti ár:')
          .replace(/,?\s+now\b/, ', most');
        if (text !== node.nodeValue) node.nodeValue = text;
      }
    });
  }

  disconnectedCallback() {
    this.formObserver?.disconnect();
    this.footerObserver?.disconnect();
    this.aovObserver?.disconnect();
    document.removeEventListener(ThemeEvents.quantityBreaksUpdated, this.onQuantityBreaks);
    document.removeEventListener(ThemeEvents.variantUpdated, this.onVariant);
  }

  #initObservers() {
    this.formObserver = new IntersectionObserver(([entry]) => {
      this.formInView = entry.isIntersecting;
      this.#updateDisplay();
    });

    this.formObserver.observe(this.form);

    if (!this.footer) return;

    this.footerObserver = new IntersectionObserver(([entry]) => {
      this.footerInView = entry.isIntersecting;
      this.#updateDisplay();
    });

    this.footerObserver.observe(this.footer);
  }

  #updateDisplay() {
    const shouldBeActive = !this.formInView && !this.footerInView;
    const isActive = this.dataset.active === 'true';

    if (shouldBeActive === isActive) return;

    clearTimeout(this.closingTimeout);

    if (shouldBeActive) {
      delete this.dataset.closing;
      this.dataset.active = 'true';
      this.content.classList.add('color-scheme-1');
      this.#syncBundle();
      return;
    }

    this.dataset.active = 'false';
    this.dataset.closing = 'true';

    this.closingTimeout = setTimeout(() => {
      delete this.dataset.closing;
      this.content.classList.remove('color-scheme-1');
    }, 300);
  }

  // Nouvelle variante : photo, libellé et prix unitaire de la barre.
  #onVariantUpdated(event) {
    const variant = event?.detail?.resource;
    if (!variant || !this.form?.contains(event.target)) return;

    const priceElement = this.querySelector('[data-ref="sticky-price"]');
    if (priceElement && variant.price) priceElement.dataset.unitPrice = String(variant.price);

    const option = this.querySelector('.sticky-add-to-cart__option');
    if (option && variant.public_title) option.textContent = variant.public_title;

    const image = this.querySelector('.sticky-add-to-cart__img');
    const src = variant.featured_media?.preview_image?.src || variant.featured_image?.src;
    if (image && src) {
      const url = new URL(src, window.location.origin);
      url.searchParams.set('width', '120');
      image.src = url.toString();
    }

    // Les paliers sont recalculés juste après la nouvelle variante.
    setTimeout(() => this.#syncBundle(), 80);
  }

  #syncBundle() {
    const priceElement = this.querySelector('[data-ref="sticky-price"]');
    const qtyElement = this.querySelector('[data-ref="sticky-qty"]');
    if (!priceElement || !qtyElement) return;

    const unit = Number(priceElement.dataset.unitPrice);
    if (!unit) return;

    let quantity = 1;
    let total = unit;
    const root = this.closest('.product-section__product-info') || this.form;
    const widget = root?.querySelector('aov-bundle-widget');
    const aovTier = widget?.querySelector('.Avada-Volume__Item--Selected');
    if (aovTier) {
      // Widget AOV : le prix d'un palier est le total du palier ; si l'app affiche encore les prix d'une
      // autre variante, on applique la même proportion au prix de la variante choisie.
      const label = aovTier.querySelector('.Avada-Volume__Info--TriggerQty')?.textContent || '';
      quantity = Math.max(1, parseInt(label.match(/\d+/)?.[0] || '1', 10));
      const offer = parseCents(aovTier.querySelector('.AOV-Offer__DiscountPrice')?.textContent);
      const base = parseCents(widget.querySelector('.Avada-Volume__Item .AOV-Offer__DiscountPrice')?.textContent);
      total = offer && base ? (base === unit ? offer : Math.round((unit * offer) / base)) : unit * quantity;
    } else {
      const breaks = this.form?.querySelector('quantity-breaks');
      const tier = breaks && !breaks.classList.contains('quantity-breaks--hidden') ? breaks.querySelector('.quantity-break--selected') : null;
      quantity = Math.max(1, parseInt(tier?.dataset.quantity || '1', 10));
      const tierTotal = parseInt(tier?.dataset.totalPrice || '', 10);
      total = Number.isFinite(tierTotal) && tierTotal > 0 ? tierTotal : unit * quantity;
    }
    const full = unit * quantity;

    const set = (element, text) => {
      if (element && element.textContent !== text) element.textContent = text;
    };
    set(qtyElement, quantity > 1 ? `${quantity} ${qtyElement.dataset.unitLabel || ''}`.trim() : '');
    set(priceElement.querySelector('.sticky-add-to-cart__total'), formatMoney(total));
    set(priceElement.querySelector('.sticky-add-to-cart__compare'), full > total ? formatMoney(full) : '');

    // Pastille « ×2 » sur la photo (téléphone).
    const media = this.querySelector('[data-ref="sticky-media"]');
    if (media) {
      if (quantity > 1) media.dataset.qty = String(quantity);
      else delete media.dataset.qty;
    }
  }
}

if (!customElements.get('sticky-add-to-cart')) {
  customElements.define('sticky-add-to-cart', StickyAddToCart);
}
